import { Pencil } from 'lucide-react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { IconButton } from '@/components/ui/button.tsx';
import { usePatientDetailStore } from '@patientMenu/hooks/usePatientDetailStore';
import { cn } from '@/lib/utils';

interface NotesPanelProps {
  className?: string;
  /** The note belongs to the clinic; the admin console only reads it. */
  readOnly?: boolean;
}

export default function NotesPanel({ className, readOnly }: NotesPanelProps) {
  const { selectedScan } = usePatientDetailStore();
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  if (!selectedScan) {
    return null;
  }

  const hasNote = Boolean(selectedScan.note?.trim());

  return (
    <section
      className={cn(
        'flex h-full min-h-0 w-full flex-col gap-3 rounded-3xl border border-content-highlight/35 bg-content-dark/45 p-4 text-white shadow-inner shadow-primary/10 backdrop-blur lg:p-5',
        className,
      )}
    >
      <header className="flex shrink-0 items-center justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-white/60">Notes</p>
          <h3 className="text-lg font-semibold tracking-tight">Treatment notes</h3>
        </div>
        {!readOnly && (
          <IconButton
            icon={Pencil}
            variant="ghost-light"
            size="icon"
            className="rounded-xl border border-content-highlight/40 bg-content-dark/70"
            onClick={() => navigate(`/patients/${patientId}/scans/${selectedScan.id}/note/edit`, { state: { backgroundLocation: location } })}
          />
        )}
      </header>

      <div className="min-h-[6rem] flex-1 rounded-2xl border border-content-highlight/30 bg-content-dark/60 px-4 py-4 text-sm leading-relaxed text-white/80">
        {hasNote ? (
          selectedScan.note
        ) : (
          <span className="text-white/50">
            {readOnly ? 'The clinic has not written any notes for this scan.' : 'No notes yet. Click the pencil to add observations.'}
          </span>
        )}
      </div>
    </section>
  );
}
